import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../../../../environments/environment';

import {
  PayCode,
  PayCodeListFilters,
  PayCodePayload,
  PayCodeType,
} from '../models/pay-codes.model';

import {
  PayCodesResponse,
  PayCodeTypesResponse,
} from '../models/pay-codes-response.model';

@Injectable({ providedIn: 'root' })
export class PayCodesService {
  private readonly baseUrl = environment.apiBaseUrl;

  constructor(private readonly http: HttpClient) {}

  getPayCodes(
    pageNumber: number = 1,
    pageSize: number = 25,
    orderBy: string = 'name',
    filters?: PayCodeListFilters,
  ): Observable<PayCodesResponse | null> {
    let params = new HttpParams()
      .set('pageNumber', String(pageNumber))
      .set('pageSize', String(pageSize))
      .set('orderBy', orderBy);

    const searchTerm = String(filters?.searchTerm ?? '').trim();

    if (searchTerm) {
      params = params.set('searchTerm', searchTerm);
    }

    return this.http
      .get<PayCodesResponse>(`${this.baseUrl}/api/pay-codes`, { params })
      .pipe(
        catchError((err) => {
          console.error('Error fetching pay codes:', err);
          return of(null);
        }),
      );
  }

  getPayCodeTypes(): Observable<PayCodeType[]> {
    return this.http
      .get<PayCodeTypesResponse>(`${this.baseUrl}/api/pay-codes/get-types`)
      .pipe(
        map((res) => res.data as PayCodeType[]),
        catchError((err) => {
          console.error('Error fetching pay code types:', err);
          return of([]);
        }),
      );
  }

  getPayCodeById(id: number): Observable<PayCode | null> {
    return this.http
      .get<{ status: string; data: PayCode }>(`${this.baseUrl}/api/pay-codes/${id}`)
      .pipe(
        map((res) => res.data),
        catchError((err) => {
          console.error('Error fetching pay code by ID:', err);
          return of(null);
        }),
      );
  }

  createPayCode(payload: PayCodePayload): Observable<boolean> {
    return this.http
      .post<{ status: string }>(`${this.baseUrl}/api/pay-codes`, payload)
      .pipe(
        map(() => true),
        catchError((err) => {
          console.error('Error creating pay code:', err);
          return of(false);
        }),
      );
  }

  updatePayCode(id: number, payload: PayCodePayload): Observable<boolean> {
    return this.http
      .patch<{ status: string }>(`${this.baseUrl}/api/pay-codes/${id}`, payload)
      .pipe(
        map(() => true),
        catchError((err) => {
          console.error('Error updating pay code:', err);
          return of(false);
        }),
      );
  }

  activatePayCode(id: number): Observable<boolean> {
    return this.http
      .patch<{ status: string }>(`${this.baseUrl}/api/pay-codes/${id}/activate`, {})
      .pipe(
        map(() => true),
        catchError((err) => {
          console.error('Error activating pay code:', err);
          return of(false);
        }),
      );
  }

  deactivatePayCode(id: number): Observable<boolean> {
    return this.http
      .patch<{ status: string }>(`${this.baseUrl}/api/pay-codes/${id}/deactivate`, {})
      .pipe(
        map(() => true),
        catchError((err) => {
          console.error('Error deactivating pay code:', err);
          return of(false);
        }),
      );
  }

  deletePayCode(id: number): Observable<boolean> {
    return this.http
      .delete<{ status: string }>(`${this.baseUrl}/api/pay-codes/${id}`)
      .pipe(
        map(() => true),
        catchError((err) => {
          console.error('Error deleting pay code:', err);
          return of(false);
        }),
      );
  }
}